import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import '../job-board.css';
import { jbApi } from '../api';
import { useJBAuth } from '../auth';
import EmployerGuard from '../components/EmployerGuard';

const STATUS_STEPS = ['applied','reviewed','interview','offered','rejected'];
const STEP_COLORS  = {
  applied:'#0D2137', reviewed:'#1B6B72', interview:'#2563eb',
  offered:'#166534', rejected:'#991b1b', withdrawn:'#64748b',
};

const LOGO_COLORS = ['#1B6B72','#0D2137','#C9A84C','#2563eb','#7c3aed','#059669'];
function logoColor(name='') {
  const n = [...name].reduce((s,c)=>s+c.charCodeAt(0),0);
  return LOGO_COLORS[n % LOGO_COLORS.length];
}

function PipelineContent({ auth }) {
  const { job_id } = useParams();
  const { token }  = auth;

  const [job, setJob]           = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [filter, setFilter]     = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes]       = useState({});
  const [savingNotes, setSavingNotes] = useState({});
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!token || !job_id) return;
    Promise.all([
      jbApi.getJobApplicants(job_id, token),
      jbApi.employerJobs(token),
    ]).then(([a, j]) => {
      setApplicants(a.applicants || []);
      const found = (j.jobs || []).find(x => x.id === job_id);
      setJob(found || null);
      // Pre-populate notes
      const n = {};
      (a.applicants || []).forEach(ap => { if (ap.employer_notes) n[ap.id] = ap.employer_notes; });
      setNotes(n);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token, job_id]);

  async function updateStatus(appId, status) {
    try {
      const updated = await jbApi.updateAppStatus(appId, { status }, token);
      setApplicants(prev => prev.map(a => a.id === appId ? { ...a, status: updated.status } : a));
    } catch {}
  }

  async function saveNote(appId) {
    setSavingNotes(p => ({ ...p, [appId]: true }));
    try {
      await jbApi.updateAppStatus(appId, { status: applicants.find(a=>a.id===appId)?.status, employer_notes: notes[appId] || '' }, token);
    } catch {}
    finally { setSavingNotes(p => ({ ...p, [appId]: false })); }
  }

  const displayed = filter === 'all' ? applicants : applicants.filter(a => a.status === filter);

  // Count per status
  const counts = STATUS_STEPS.reduce((acc, s) => {
    acc[s] = applicants.filter(a => a.status === s).length;
    return acc;
  }, {});

  return (
    <div className="jb-page" data-testid="applicant-pipeline-page">
      {/* Header */}
      <div style={{ background:'#0D2137', padding:'16px 0' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', padding:'0 24px' }}>
          <Link to="/employer/jobs" style={{ fontSize:11, color:'rgba(245,240,232,.6)', textDecoration:'none' }}>← My Listings</Link>
          <div style={{ fontSize:16, fontWeight:700, color:'#F5F0E8', marginTop:2 }}>
            {job ? `${job.title} — Applicants` : 'Applicants'}
            <span style={{ marginLeft:12, fontSize:13, color:'rgba(245,240,232,.5)', fontWeight:500 }}>
              {applicants.length} total
            </span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:'0 auto', padding:'24px 24px 60px' }}>

        {/* Pipeline status strip */}
        <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
          <button onClick={() => setFilter('all')} style={{ padding:'6px 14px', borderRadius:999, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'Inter,sans-serif', border: filter==='all'?'1.5px solid #1B6B72':'1.5px solid #ddd8cc', background: filter==='all'?'rgba(27,107,114,.08)':'#fff', color: filter==='all'?'#1B6B72':'#5a6373' }}>
            All ({applicants.length})
          </button>
          {STATUS_STEPS.map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{ padding:'6px 14px', borderRadius:999, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'Inter,sans-serif', border: filter===s?`1.5px solid ${STEP_COLORS[s]}`:'1.5px solid #ddd8cc', background: filter===s?`rgba(0,0,0,.04)`:'#fff', color: filter===s?STEP_COLORS[s]:'#5a6373' }}>
              {s.charAt(0).toUpperCase()+s.slice(1)} ({counts[s] || 0})
            </button>
          ))}
        </div>

        {loading && <div style={{ textAlign:'center', padding:40, color:'#5a6373' }}>Loading applicants…</div>}

        {!loading && displayed.length === 0 && (
          <div style={{ background:'#fff', borderRadius:10, padding:40, textAlign:'center', boxShadow:'0 2px 8px rgba(13,33,55,.07)' }}>
            <div style={{ fontSize:13, color:'#5a6373' }}>No applicants {filter !== 'all' ? `in "${filter}" stage` : 'yet'}.</div>
          </div>
        )}

        {displayed.map(app => {
          const s = app.seeker_profile || {};
          const name = `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Unknown';
          const bg = logoColor(name);
          const isExpanded = expanded === app.id;

          return (
            <div key={app.id} data-testid={`applicant-${app.id}`} style={{ background:'#fff', borderRadius:10, marginBottom:12, boxShadow:'0 2px 8px rgba(13,33,55,.07)', overflow:'hidden' }}>
              {/* Row */}
              <div style={{ padding:'14px 18px', display:'flex', alignItems:'center', gap:14, cursor:'pointer' }} onClick={() => setExpanded(isExpanded ? null : app.id)}>
                {/* Avatar */}
                <div style={{ width:40, height:40, borderRadius:10, background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:'#fff', flexShrink:0 }}>
                  {name.slice(0,2).toUpperCase()}
                </div>
                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:'#0D2137', marginBottom:3 }}>{name}</div>
                  <div style={{ fontSize:12, color:'#5a6373' }}>{s.email || 'No email'} · {s.location || 'Location unknown'}</div>
                </div>
                {/* Status selector */}
                <select
                  value={app.status}
                  onChange={e => { e.stopPropagation(); updateStatus(app.id, e.target.value); }}
                  onClick={e => e.stopPropagation()}
                  data-testid={`status-select-${app.id}`}
                  style={{ padding:'6px 10px', borderRadius:7, border:`1.5px solid ${STEP_COLORS[app.status] || '#ddd8cc'}`, background:'#fff', fontSize:12, fontWeight:700, color:STEP_COLORS[app.status] || '#5a6373', cursor:'pointer', fontFamily:'Inter,sans-serif' }}
                >
                  {STATUS_STEPS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                  <option value="withdrawn">Withdrawn</option>
                </select>
                <span style={{ color:'#94a3b8', fontSize:16 }}>{isExpanded ? '▲' : '▼'}</span>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div style={{ padding:'14px 18px 18px', borderTop:'1px solid #f5f0e8', background:'#fafaf9' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:'#5a6373', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>Seeker Profile</div>
                      {s.headline && <div style={{ fontSize:13, color:'#0D2137', marginBottom:4 }}>{s.headline}</div>}
                      {s.linkedin_url && <a href={s.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'#1B6B72', display:'block', marginBottom:4 }}>LinkedIn Profile →</a>}
                      {app.resume_url && <a href={app.resume_url} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'#1B6B72', display:'block', marginBottom:4 }}>View Resume →</a>}
                      {s.sectors_of_interest?.length > 0 && (
                        <div style={{ fontSize:12, color:'#5a6373', marginTop:4 }}>Sectors: {s.sectors_of_interest.join(', ')}</div>
                      )}
                    </div>
                    <div>
                      {app.cover_note && (
                        <>
                          <div style={{ fontSize:11, fontWeight:700, color:'#5a6373', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>Cover Note</div>
                          <div style={{ fontSize:13, color:'#374151', lineHeight:1.6, background:'#fff', borderRadius:8, padding:'10px 12px', border:'1px solid #e5e0d6' }}>{app.cover_note}</div>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Notes */}
                  <div style={{ marginTop:14 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#5a6373', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:6 }}>Employer Notes</div>
                    <div style={{ display:'flex', gap:8 }}>
                      <textarea
                        value={notes[app.id] || ''}
                        onChange={e => setNotes(p => ({ ...p, [app.id]: e.target.value }))}
                        placeholder="Private notes visible only to you…"
                        data-testid={`notes-${app.id}`}
                        style={{ flex:1, padding:'8px 10px', borderRadius:7, border:'1.5px solid #ddd8cc', fontSize:13, resize:'vertical', minHeight:60, fontFamily:'Inter,sans-serif', outline:'none' }}
                      />
                      <button onClick={() => saveNote(app.id)} data-testid={`save-notes-${app.id}`} disabled={savingNotes[app.id]} style={{ padding:'8px 14px', borderRadius:7, background:'#1B6B72', color:'#fff', border:'none', fontSize:12, fontWeight:700, cursor:'pointer', alignSelf:'flex-start', fontFamily:'Inter,sans-serif' }}>
                        {savingNotes[app.id] ? '…' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ApplicantPipelinePage() {
  const auth = useJBAuth();
  return <EmployerGuard><PipelineContent auth={auth} /></EmployerGuard>;
}
